import axios from "axios"

export interface EmailAttachment {
  filename: string
  content: string
  encoding?: string
  contentType?: string
}

export interface EmailResponse {
  success: boolean
  message: string
  messageId?: string
  error?: string
  sentTo?: string
  sentAt?: string
}

export interface RegisterUserResponse {
  success: boolean
  message: string
  userId?: string
  error?: string
}

export default class MailApi {
  private readonly registerUserUrl = "https://registeruser-p2xodpgboa-uc.a.run.app"
  private readonly sendMailUrl = "https://sendmail-p2xodpgboa-uc.a.run.app"
  private readonly sendMailUploadUrl = "https://sendmailupload-p2xodpgboa-uc.a.run.app"
  private readonly sendScorecardEmailUrl = "https://sendscorecardemail-p2xodpgboa-uc.a.run.app"
  private readonly sendMailCCUrl = "https://sendmailcc-p2xodpgboa-uc.a.run.app"
  private readonly helloWorldUrl = "https://helloworld-p2xodpgboa-uc.a.run.app"

  constructor() {
    // No initialization needed with individual URLs
  }

  async registerUser(userData: {
    email: string
    name?: string
    [key: string]: any
  }): Promise<RegisterUserResponse> {
    try {
      const response = await axios.post(this.registerUserUrl, userData)
      return response.data
    } catch (error) {
      console.error("Error registering user:", error)
      throw new Error(`Failed to register user: ${error}`)
    }
  }

  async helloWorld(): Promise<any> {
    try {
      const response = await axios.get(this.helloWorldUrl)
      return response.data
    } catch (error) {
      console.error("Error calling helloWorld:", error)
      throw new Error(`Failed to call helloWorld: ${error}`)
    }
  }

  async sendMail(to: string[], from: string, cc: string[], subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(this.sendMailUrl, {
        to,
        from,
        cc,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email:", error)
      throw new Error(`Failed to send email: ${error}`)
    }
  }

  async sendMailUpload(
    to: string[],
    from: string,
    cc: string[],
    subject: string,
    message: string,
    ticketNumber: string,
    attachments?: EmailAttachment[],
  ): Promise<EmailResponse> {
    try {
      const response = await axios.post(this.sendMailUploadUrl, {
        to,
        from,
        cc,
        subject,
        message,
        ticketNumber,
        attachments,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email with upload:", error)
      throw new Error(`Failed to send email with upload: ${error}`)
    }
  }

  async scorecardMail(
    to: string,
    from: string,
    subject: string,
    message: string,
    pdfContent?: string,
    htmlContent?: string,
    filename?: string,
  ): Promise<EmailResponse> {
    try {
      const response = await axios.post(this.sendScorecardEmailUrl, {
        to,
        from,
        subject,
        message,
        pdfContent,
        htmlContent,
        filename,
      })
      return response.data
    } catch (error) {
      console.error("Error sending scorecard email:", error)
      throw new Error(`Failed to send scorecard email: ${error}`)
    }
  }

  async sendMailCC(to: string[], cc: string[], from: string, subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(this.sendMailCCUrl, {
        to,
        cc,
        from,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending email with CC:", error)
      throw new Error(`Failed to send email with CC: ${error}`)
    }
  }

  async sendMailfeeback(to: string[], from: string, subject: string, message: string): Promise<EmailResponse> {
    try {
      const response = await axios.post(this.sendMailUrl, {
        to,
        from,
        subject,
        message,
      })
      return response.data
    } catch (error) {
      console.error("Error sending feedback email:", error)
      throw new Error(`Failed to send feedback email: ${error}`)
    }
  }

  // Utility method to convert file to base64 for attachments
  static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64 = reader.result as string
        // Remove data:mime/type;base64, prefix
        const base64Data = base64.split(",")[1]
        resolve(base64Data)
      }
      reader.onerror = (error) => reject(error)
    })
  }
}
